import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { BedDouble, Settings, Download, Loader2, Search, DollarSign, Clock, CheckCircle2, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ReservationSettingsDialog } from './reservations/ReservationSettingsDialog';
import { ReservationRow } from './reservations/ReservationRow';
import { SeasonPanel } from './reservations/SeasonPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });
const FILTERS = [['pending', 'Pending'], ['confirmed', 'Confirmed'], ['expired', 'Expired'], ['cancelled', 'Cancelled'], ['all', 'All']];

const Kpi = ({ label, value, sub, Icon, tone }) => (
  <Card className="border-0 shadow-sm"><CardContent className="p-5 flex items-start justify-between">
    <div><p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">{label}</p><p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>{sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}</div>
    <div className={`p-3 rounded-xl ${tone}`}><Icon className="w-5 h-5" /></div>
  </CardContent></Card>
);

const ReservationsPage = () => {
  const [data, setData] = useState(null);
  const [season, setSeason] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [res, seasonRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reserve/admin/list`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/reserve/admin/season`, { headers: getAuthHeaders() }),
      ]);
      setData(res.data);
      setSeason(seasonRes.data);
    } catch { toast.error('Failed to load reservations'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (action, r, note = '') => {
    if (action === 'cancel' && !window.confirm(`Release ${r.first_name} ${r.last_name}'s ${r.tier_name} bed (${r.code})?`)) return;
    if (action === 'delete' && !window.confirm(`Permanently delete ${r.code}?`)) return;
    setBusy(r.id);
    try {
      if (action === 'delete') await axios.delete(`${BACKEND_URL}/api/reserve/admin/${r.id}`, { headers: getAuthHeaders() });
      else await axios.post(`${BACKEND_URL}/api/reserve/admin/${r.id}/${action}`, { note }, { headers: getAuthHeaders() });
      toast.success({ confirm: `Confirmed ${r.code} — bed locked, receipt emailed`, extend: `Hold extended for ${r.code}`, cancel: `Bed released (${r.code})`, delete: 'Deleted' }[action]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); } finally { setBusy(null); }
  };

  const saveSettings = async (form) => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/reserve/admin/settings`, form, { headers: getAuthHeaders() });
      toast.success('Settings saved'); setShowSettings(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const exportCsv = async () => {
    const res = await axios.get(`${BACKEND_URL}/api/reserve/admin/export.csv`, { headers: getAuthHeaders(), responseType: 'blob' });
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(res.data), download: `reservations-${data.settings.season}.csv` });
    a.click(); URL.revokeObjectURL(a.href);
  };

  if (!data) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-wine" /></div>;

  const all = data.reservations;
  const pending = all.filter(r => r.status === 'pending');
  const confirmed = all.filter(r => r.status === 'confirmed');
  const rows = all.filter(r => (filter === 'all' || r.status === filter) && (!q.trim() || `${r.first_name} ${r.last_name} ${r.email} ${r.code}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="reservations-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{data.settings.season} Reservations</h1>
          <p className="text-gray-600 mt-1">Venmo holds to verify, beds locked, and tier capacity. {data.settings.open ? <span className="text-emerald-700 font-medium">Open to the public</span> : <span className="text-amber-700 font-medium">Closed</span>} · <a href="/reserve" target="_blank" rel="noreferrer" className="text-wine underline inline-flex items-center gap-1">public page <ExternalLink className="w-3 h-3" /></a></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={exportCsv} data-testid="export-reservations-btn"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
          <Button onClick={() => setShowSettings(true)} className="bg-gradient-to-r from-wine to-burgundy text-white" data-testid="reservation-settings-btn"><Settings className="w-4 h-4 mr-2" />Tiers & Settings</Button>
        </div>
      </div>

      <SeasonPanel season={season} onChanged={load} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi label="Awaiting Venmo" value={pending.length} sub={`${pending.filter(r => r.payment_reported_at).length} say they've paid`} Icon={Clock} tone="bg-amber-50 text-amber-600" />
        <Kpi label="Confirmed beds" value={confirmed.length} Icon={CheckCircle2} tone="bg-emerald-50 text-emerald-600" />
        <Kpi label="Revenue locked" value={`$${(data.confirmed_revenue || 0).toLocaleString()}`} sub={`$${pending.reduce((s, r) => s + r.price, 0).toLocaleString()} pending`} Icon={DollarSign} tone="bg-wine-50 text-wine" />
        <Kpi label="Beds open" value={data.tiers.reduce((s, t) => s + t.available, 0)} sub={`of ${data.tiers.reduce((s, t) => s + t.capacity, 0)} total`} Icon={BedDouble} tone="bg-blue-50 text-blue-600" />
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6" data-testid="tier-capacity-cards">
        {data.tiers.map(t => {
          const used = t.pending + t.confirmed;
          const pct = t.capacity ? Math.min(100, Math.round(used / t.capacity * 100)) : 0;
          return (
            <Card key={t.id} className="border-0 shadow-sm" data-testid={`tier-capacity-${t.id}`}><CardContent className="p-5">
              <div className="flex items-baseline justify-between mb-2"><p className="font-semibold text-gray-900">{t.name}</p><p className="text-sm text-gray-500">${t.price}</p></div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden flex">
                <div className="bg-emerald-500 h-full" style={{ width: `${t.capacity ? t.confirmed / t.capacity * 100 : 0}%` }} />
                <div className="bg-amber-400 h-full" style={{ width: `${t.capacity ? t.pending / t.capacity * 100 : 0}%` }} />
              </div>
              <p className="text-xs text-gray-500 mt-2">{t.confirmed} confirmed · {t.pending} pending · <span className={t.available === 0 ? 'text-red-600 font-semibold' : 'text-gray-700 font-medium'}>{t.available} open</span> · {pct}% full</p>
            </CardContent></Card>
          );
        })}
      </div>

      <Card className="border-0 shadow-sm"><CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 flex-wrap">
            {FILTERS.map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`res-filter-${k}`}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${filter === k ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}>
                {label} ({k === 'all' ? all.length : all.filter(r => r.status === k).length})
              </button>
            ))}
          </div>
          <div className="relative sm:ml-auto sm:w-72">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Name, email or code" className="pl-9" data-testid="res-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b">
              <th className="py-2 pr-3 font-semibold">Code</th><th className="py-2 pr-3 font-semibold">Attendee</th><th className="py-2 pr-3 font-semibold">Tier</th><th className="py-2 pr-3 font-semibold">Status</th><th className="py-2 font-semibold text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={5} className="py-10 text-center text-gray-400 italic">Nothing here yet</td></tr>
                : rows.map(r => <ReservationRow key={r.id} r={r} onAction={act} busy={busy} />)}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {showSettings && <ReservationSettingsDialog open onOpenChange={setShowSettings} settings={data.settings} onSave={saveSettings} saving={saving} />}
    </div>
  );
};

export default ReservationsPage;
